import React, { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "../ui/card";
import CarImg from "../../assets/CARRO.svg"; // <-- import do carro

export default function TimelineSection({ timeline }) {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [vehiclePos, setVehiclePos] = useState({ left: 0, top: 0 });

  const sectionRef = useRef(null);
  const trackWrapRef = useRef(null);
  const trackRef = useRef(null);
  const trackCenterRef = useRef(null);
  const vehicleRef = useRef(null);
  const cardRefs = useRef([]);

  // Detecta se é mobile
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lógica de scroll + posição do carro
  useEffect(() => {
    cardRefs.current = timeline.map(() => React.createRef());

    const update = () => {
      if (!sectionRef.current || !trackWrapRef.current || !trackRef.current || !vehicleRef.current || !trackCenterRef.current) {
        return;
      }

      const sec = sectionRef.current.getBoundingClientRect();
      const secTop = sec.top + window.scrollY;
      const secH = sec.height;
      const vh = window.innerHeight;
      const secP = Math.max(
        0,
        Math.min(
          1,
          (window.scrollY - (secTop - vh + 200)) /
            (secTop + secH - 200 - (secTop - vh + 200))
        )
      );

      const newVisible = new Set();
      const itemsToShow = Math.floor(secP * timeline.length * 1.2);
      for (let i = 0; i < Math.min(itemsToShow, timeline.length); i++)
        newVisible.add(i);
      setVisibleItems(newVisible);

      const wrapRect = trackWrapRef.current.getBoundingClientRect();
      const trackRect = trackRef.current.getBoundingClientRect();
      const centerRect = trackCenterRef.current.getBoundingClientRect();

      const vehicleH = vehicleRef.current.offsetHeight || 0;

      const trackTopGlobal = trackRect.top + window.scrollY;
      const centerOfViewport = window.scrollY + vh / 2;
      let p = (centerOfViewport - trackTopGlobal) / trackRect.height;
      p = Math.max(0, Math.min(1, p));

      const trackTopInWrap = trackRect.top - wrapRect.top;
      const usableH = Math.max(0, trackRect.height - vehicleH);

      const MOBILE_OFFSET_Y = 180;
      const extraY = isMobile ? MOBILE_OFFSET_Y : 0;

      let topInWrap = trackTopInWrap + usableH * p + extraY;
      const minTop = trackTopInWrap;
      const maxTop = trackTopInWrap + usableH;
      topInWrap = Math.max(minTop, Math.min(maxTop, topInWrap));

      if (isMobile) {
        setVehiclePos((prev) => ({ left: prev.left, top: Math.round(topInWrap) }));
      } else {
        const centerXInWrap = centerRect.left - wrapRect.left;
        setVehiclePos({ left: Math.round(centerXInWrap), top: Math.round(topInWrap) });
      }
    };

    const onScroll = () => update();
    const onResize = () => update();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    requestAnimationFrame(update);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [timeline, isMobile]);

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-x-clip"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-24">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Marcos do Transporte Rodoviário 2024
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Acompanhe a jornada dos principais acontecimentos do setor
            rodoviário brasileiro
          </p>
        </div>

        <div ref={trackWrapRef} className="relative">
          {/* Pavimento */}
          <div
            ref={trackRef}
            className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-24 bg-gray-600 shadow-lg z-10 rounded"
          >
            <div className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-1 border-dashed border-l-4 border-yellow-300 h-full" />
            <div className="absolute left-4 top-0 bottom-0 w-1 bg-gray-500 h-full" />
            <div className="absolute right-4 top-0 bottom-0 w-1 bg-gray-500 h-full" />
            <div
              ref={trackCenterRef}
              className="absolute left-1/2 top-0 w-px h-full pointer-events-none"
            />
          </div>

          {/* Carro (SVG importado) */}
          <div
            ref={vehicleRef}
            className={`${isMobile ? "z-10" : "z-20"} absolute transition-[top,left] duration-150 ease-out`}
            style={{
              left: isMobile ? "50%" : `${vehiclePos.left}px`,
              top: `${vehiclePos.top}px`,
            }}
          >
            <div className="-translate-x-1/2 w-28 h-28">
              <img src={CarImg} alt="Carro" className="w-full h-full" />
            </div>
          </div>

          {/* Itens da timeline */}
          <div
            className={`${isMobile ? "space-y-0" : "space-y-4"} relative ${
              isMobile ? "z-20" : ""
            }`}
          >
            {timeline.map((item, index) => {
              const isLeft = index % 2 === 0;
              const isVisible = visibleItems.has(index);

              if (isMobile) {
                return (
                  <div
                    key={index}
                    className="min-h-[80vh] flex items-center justify-center"
                  >
                    <div
                      ref={cardRefs.current[index]}
                      className={`w-[90%] max-w-md transition-all duration-500 ease-out ${
                        isVisible
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-6"
                      }`}
                    >
                      <Card className="shadow-xl border-0 bg-white/85 backdrop-blur-sm">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                              <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center text-3xl shadow border border-blue-200">
                                {item.icon}
                              </div>
                            </div>
                            <div className="flex-1 text-left">
                              <span className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow inline-block mb-3">
                                {item.month}
                              </span>
                              <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                                {item.title}
                              </h3>
                              <p className="text-gray-700 leading-relaxed">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={index}
                  className={`flex items-center min-h-[250px] ${
                    isLeft ? "justify-start" : "justify-end"
                  }`}
                >
                  <div
                    ref={cardRefs.current[index]}
                    className={`w-1/2 transition-all duration-700 ease-in-out ${
                      isVisible
                        ? "opacity-100 translate-x-0"
                        : `opacity-0 ${isLeft ? "-translate-x-10" : "translate-x-10"}`
                    }`}
                  >
                    <div className={`relative ${isLeft ? "pr-20" : "pl-20"}`}>
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 h-0.5 bg-gradient-to-r ${
                          isLeft
                            ? "right-0 w-20 from-blue-300 to-gray-400"
                            : "left-0 w-20 from-gray-400 to-blue-300"
                        }`}
                      />
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 border-blue-400 shadow-lg ${
                          isLeft ? "right-20" : "left-20"
                        }`}
                      />

                      <Card className="hover:shadow-2xl transition-all duration-300 border-0 shadow-lg">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center text-3xl shadow-lg border border-blue-200">
                                {item.icon}
                              </div>
                            </div>
                            <div className="flex-1 text-left">
                              <span className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg inline-block mb-3">
                                {item.month}
                              </span>
                              <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                                {item.title}
                              </h3>
                              <p className="text-gray-600 leading-relaxed">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
